import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Coins, ArrowCounterClockwise, Check, Plus } from "@phosphor-icons/react";

// Common top-up countries for quick add (code + name). You can add any ISO-2 code.
const COMMON = [
  ["CM", "Cameroon"], ["NG", "Nigeria"], ["GH", "Ghana"], ["KE", "Kenya"], ["US", "United States"],
  ["IN", "India"], ["PH", "Philippines"], ["MX", "Mexico"], ["SN", "Senegal"], ["CI", "Côte d'Ivoire"],
];

export default function AdminTopupMargins() {
  const qc = useQueryClient();
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState("");
  const [form, setForm] = useState({ country: "CM", name: "Cameroon", markup_pct: "" });

  const { data } = useQuery({ queryKey: ["topup-margins"], queryFn: async () => (await api.get("/admin/topup-margins")).data });
  const margins = data?.margins || [];

  const refresh = () => qc.invalidateQueries({ queryKey: ["topup-margins"] });

  const save = async (country, name, pctRaw) => {
    const pct = parseFloat(pctRaw);
    if (!(pct >= 0)) { toast.error("Enter a markup % (0 or more)"); return; }
    setSaving(country);
    try {
      await api.put(`/admin/topup-margins/${country}`, { markup_pct: pct, name });
      toast.success("Margin saved — applies to new top-ups");
      setEdits((e) => { const n = { ...e }; delete n[country]; return n; });
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  const reset = async (country) => {
    setSaving(country);
    try {
      await api.delete(`/admin/topup-margins/${country}`);
      toast.success("Removed — reverts to provider retail");
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  const add = async () => {
    const code = form.country.trim().toUpperCase();
    if (code.length < 2) { toast.error("Enter a 2-letter country code"); return; }
    await save(code, form.name.trim() || code, form.markup_pct);
    setForm({ country: "", name: "", markup_pct: "" });
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-topup-margins">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Top-Up Margins</h1>
        <p className="mt-1 text-sm text-muted-foreground">Set your airtime markup per country (% over wholesale). Applies to new top-ups immediately — countries without a rule use the provider's retail price.</p>
      </div>

      {/* Add / set a country margin */}
      <div className="rounded-2xl border border-white/10 bg-card p-4" data-testid="add-margin">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Plus size={16} weight="bold" /> Set a country margin</h2>
        <div className="grid gap-2 sm:grid-cols-[110px_1fr_120px_auto]">
          <input data-testid="margin-country" value={form.country} onChange={(e) => {
            const c = e.target.value.toUpperCase();
            const known = COMMON.find(([k]) => k === c);
            setForm({ ...form, country: c, name: known ? known[1] : form.name });
          }} placeholder="ISO-2 (CM)" maxLength={2}
            className="rounded-lg border border-white/10 bg-background px-3 py-2 text-sm uppercase outline-none focus:border-primary" />
          <input data-testid="margin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Country name"
            className="rounded-lg border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          <input data-testid="margin-pct" type="number" min="0" step="0.5" value={form.markup_pct} onChange={(e) => setForm({ ...form, markup_pct: e.target.value })} placeholder="markup %"
            className="rounded-lg border border-white/10 bg-background px-3 py-2 text-right text-sm outline-none focus:border-primary" />
          <button data-testid="margin-add-btn" onClick={add} disabled={saving === form.country.toUpperCase()}
            className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">Save</button>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {COMMON.map(([k, n]) => (
            <button key={k} onClick={() => setForm({ ...form, country: k, name: n })}
              className="rounded-full bg-white/5 px-2.5 py-1 text-[11px] text-muted-foreground hover:text-foreground">{k} · {n}</button>
          ))}
        </div>
      </div>

      {/* Existing margins */}
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-card">
        <div className="grid grid-cols-[1fr_120px_auto] items-center gap-3 border-b border-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <span>Country</span><span className="text-right">Markup %</span><span></span>
        </div>
        {margins.map((m) => {
          const val = edits[m.country] ?? String(m.markup_pct);
          const dirty = edits[m.country] !== undefined && parseFloat(edits[m.country]) !== m.markup_pct;
          return (
            <div key={m.country} data-testid={`margin-row-${m.country}`} className="grid grid-cols-[1fr_120px_auto] items-center gap-3 border-b border-white/5 px-4 py-3 last:border-0">
              <div>
                <p className="text-sm font-medium">{m.name || m.country}</p>
                <p className="font-mono-metric text-xs text-muted-foreground">{m.country}</p>
              </div>
              <input data-testid={`margin-input-${m.country}`} type="number" min="0" step="0.5" value={val}
                onChange={(e) => setEdits((x) => ({ ...x, [m.country]: e.target.value }))}
                className="w-full rounded-lg border border-white/10 bg-background px-3 py-1.5 text-right text-sm outline-none focus:border-primary" />
              <div className="flex items-center gap-1.5">
                <button data-testid={`margin-save-${m.country}`} onClick={() => save(m.country, m.name, val)} disabled={!dirty || saving === m.country}
                  className="flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground disabled:opacity-40"><Check size={13} weight="bold" /> Save</button>
                <button data-testid={`margin-reset-${m.country}`} onClick={() => reset(m.country)} disabled={saving === m.country}
                  title="Remove" className="rounded-lg border border-white/10 p-1.5 text-muted-foreground hover:text-foreground"><ArrowCounterClockwise size={14} /></button>
              </div>
            </div>
          );
        })}
        {margins.length === 0 && <p className="flex items-center justify-center gap-2 px-4 py-10 text-center text-sm text-muted-foreground"><Coins size={16} /> No country margins yet — add one above.</p>}
      </div>
    </div>
  );
}
